import { useSnackbar } from 'notistack';
import React from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { type EnterpriseApiPermissions, useGetOrganisation, useUpdateOrganisation } from '../../../../../app/ApiGen';
import { useCurrentOrganisation } from '../../../../../app/CurrentOrganisationProvider';
import { SwitchInput, SwitchValue } from '../../../../../components/CustomFields/Switch';
import { DataErrorHandler } from '../../../../../components/ErrorHandler';
import { errorSnackbar } from '../../../../../components/ErrorSnackbar';
import { Form } from '../../../../../components/Form';
import { Card, Loading, type OnSubmit, type ValueComponentProps } from '../../../../../design-system';
import { fullStateSelector } from '../../../../../store/root';
import type { SubState } from '../../../../../store/types';
import { createSchema, type FieldDefinition, type FormFields } from '../../../../../utils/formUtils';
import { normalise } from '../../../../../utils/request';

type FormApiPermissions = Required<EnterpriseApiPermissions>;

const defaultApiPermissions: FormApiPermissions = {
  allChargePointsList: false,
  chargePointRead: false,
  chargePointSessionsList: false,
  allSessionsList: false,
  chargePointMeterValuesList: false,
  chargePointLoadManagementUpdate: false,
};

const validationSchema = createSchema<FormApiPermissions>({
  allChargePointsList: Yup.boolean().required(),
  chargePointRead: Yup.boolean().required(),
  chargePointLoadManagementUpdate: Yup.boolean().required(),
  chargePointSessionsList: Yup.boolean().required(),
  chargePointMeterValuesList: Yup.boolean().required(),
  allSessionsList: Yup.boolean().required(),
});

const formField: Pick<FieldDefinition<FormApiPermissions>, 'inputComponent' | 'valueComponent'> = {
  inputComponent: SwitchInput,
  valueComponent: ({ value }: ValueComponentProps<boolean>) => (
    <SwitchValue value={value} labels={{ true: 'Enabled', false: 'Disabled' }} />
  ),
};

const formFields: FormFields<FormApiPermissions> = {
  allChargePointsList: { ...formField, id: 'allChargePointsList', label: 'List all charge points' },
  allSessionsList: { ...formField, id: 'allSessionsList', label: 'List all sessions' },
  chargePointLoadManagementUpdate: {
    ...formField,
    id: 'chargePointLoadManagementUpdate',
    label: 'Update charge point load management',
  },
  chargePointMeterValuesList: { ...formField, id: 'chargePointMeterValuesList', label: 'List meter values' },
  chargePointRead: { ...formField, id: 'chargePointRead', label: 'Read charge point' },
  chargePointSessionsList: { ...formField, id: 'chargePointSessionsList', label: 'List charge point sessions' },
};

const FetchContext: React.FC<{ organisation: SubState<'organisation'> }> = ({ organisation }) => {
  const { mutate: updateOrg } = useUpdateOrganisation({ orgId: organisation.id });

  const { data, error, loading, refetch } = useGetOrganisation({
    orgSlug: organisation.slug,
  });
  const organisationData = data ? normalise(data) : undefined;
  const apiPermissions = organisationData?.apiPermissions ?? {};

  const { isEvnexAdmin } = useSelector(fullStateSelector);

  const [isEditing, setIsEditing] = React.useState<boolean>(false);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const initialValues = Object.keys(apiPermissions).reduce<FormApiPermissions>((acc, k) => {
    const key = k as keyof EnterpriseApiPermissions;
    return { ...acc, [key]: apiPermissions[key] ?? false };
  }, defaultApiPermissions);

  const onSave = React.useCallback<OnSubmit<FormApiPermissions>>(
    async (formPermissions) => {
      try {
        await updateOrg({
          data: {
            attributes: {
              apiPermissions: formPermissions,
            },
            type: 'organisations',
            id: organisation.id,
          },
        });
        enqueueSnackbar('API Permissions updated successfully', { variant: 'success' });
        setIsEditing(false);
        await refetch();
      } catch (e) {
        errorSnackbar('Error: save failed, please try again later', closeSnackbar, enqueueSnackbar, e);
      }
    },
    [organisation.id, updateOrg, enqueueSnackbar, closeSnackbar, refetch],
  );

  if (error) {
    return (
      <DataErrorHandler
        description={error.status === 403 ? 'Not authorized to view this content' : 'Unable to load API permissions'}
        error={error}
        refetch={refetch}
        type="embedded"
      />
    );
  }

  return (
    <Card title="API Permissions">
      <Form<FormApiPermissions>
        fields={formFields}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSave}
        isLoading={loading}
        isEditing={isEvnexAdmin ? isEditing : false}
        setIsEditing={isEvnexAdmin ? setIsEditing : undefined}
      />
    </Card>
  );
};

export const APIPermissions: React.FC = () => {
  const { organisation, loading } = useCurrentOrganisation();

  if (loading || !organisation) {
    return <Loading />;
  }

  return <FetchContext organisation={organisation} />;
};
